import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Typography,
  TextField,
  IconButton,
  Box,
  Chip,
  Grid,
  Divider,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "@tanstack/react-query";
import { getSingleProduct } from "../../../../services/gameService";
import redFocusStyles from "../../../../components/ui/formUI/RedFocusedStyleV2";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(6px)",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },
  "& .MuiPaper-root": {
    width: "500px",
    maxWidth: "90vw",
    border: `1px solid ${theme.palette.mode === "dark" ? "#444" : "#d32f2f"}`,
    borderRadius: 12,
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.mode === "dark"
        ? "#111827"
        : theme.palette.background.paper,
  },
}));

export default function ViewGameModal({ open, onClose, gameId }) {
  const { data: game, isLoading, isError } = useQuery({
    queryKey: ["singleGame", gameId],
    queryFn: () => getSingleProduct(gameId),
    enabled: !!gameId,
  });

  if (isLoading) {
    return (
      <StyledDialog open={open} onClose={onClose}>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </StyledDialog>
    );
  }

  if (isError || !game) {
    return (
      <StyledDialog open={open} onClose={onClose}>
        <DialogContent>
          <Typography color="error">
            Error loading game details.
          </Typography>
        </DialogContent>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: 0,
          pb: 2,
        }}
      >
        <Typography variant="h6">Game Details</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                value={game?.productName || ""}
                sx={redFocusStyles}
                variant="outlined"
                label="Game Name"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Grid>
            
            {/* <Grid item xs={6}>
              <TextField
                value={game?.providerDetails?.name || ""}
                sx={redFocusStyles}
                variant="outlined"
                label="Provider"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Grid> */}
            
            <Grid item xs={6}>
              <TextField
                value={game?.productTypeDetails?.productType?.en || ""}
                sx={redFocusStyles}
                variant="outlined"
                label="Game Type"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                value={game?.productCategoryDetails?.category_name?.en || ""}
                sx={redFocusStyles}
                variant="outlined"
                label="Category"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                value={`${game?.price || 0} AFN`}
                sx={redFocusStyles}
                variant="outlined"
                label="Price"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                value={game?.description || "No description available"}
                sx={redFocusStyles}
                variant="outlined"
                label="Description"
                InputLabelProps={{ shrink: true }}
                fullWidth
                multiline
                rows={2}
                disabled
              />
            </Grid>
            
            {/* {game?.tag?.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Tags:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {game.tag.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </Grid>
            )} */}
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                value={game?.status ? "Active" : "Inactive"}
                sx={redFocusStyles}
                variant="outlined"
                label="Status"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                value={game?.countryDetails?.countryName || ""}
                sx={redFocusStyles}
                variant="outlined"
                label="Country"
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled
              />
            </Grid>
            
            {game?.thirdPartyId && (
              <Grid item xs={12}>
                <TextField
                  value={game.thirdPartyId}
                  sx={redFocusStyles}
                  variant="outlined"
                  label="Third Party ID"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ bgcolor: "#d32f2f", width: "100%" }}
        >
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}